import * as React from 'react';
import {
    AutocompleteItem,
    createStyles,
    Loader,
    Popover,
    Chip,
    Button,
    Text
} from "@mantine/core";
import { useDebouncedValue, useElementSize } from "@mantine/hooks";
import axios from "axios";
import qs from "qs";
import { IResponse } from "../../types/api";
import { IconSearch } from "@tabler/icons-react";

const useStyles = createStyles((theme, width: number) => {
    return {
        inputWrapper: {
            border: `1px solid ${theme.colors.gray[3]}`,
            borderRadius: theme.radius.sm,
            padding: '.4rem .8rem',
            display: 'flex',
            alignItems: 'center',
            '&:focus-within': {
                borderColor: theme.colors[theme.primaryColor]
            }
        },
        label: {
            display: 'flex'
        },
        wrapper: {
            border: `1px solid ${theme.colors.gray[3]}`,
            borderRadius: theme.radius.sm,
            padding: '.4rem .8rem',
            display: 'flex',
            alignItems: 'center',
            '&:focus-within': {
                borderColor: theme.colors[theme.primaryColor]
            }
        },
        dropdown: {
            maxHeight: 500,
            overflowY: 'auto',
        },
        selectedItem: {
            wordBreak: 'keep-all',
            whiteSpace: 'nowrap',
            color: theme.black,
            margin: theme.spacing.xs,
            border: `1px solid ${theme.colors.gray[2]}`,
            fontSize: 13,
            cursor: 'pointer !important',
            zIndex: 12500,
        },
        input: {
            border: 0,
            outline: 'none',
            flex: 1,
            paddingLeft: theme.spacing.md,
        },
        icon: {
            marginRight: theme.spacing.xs,
            display: 'grid',
            placeItems: 'center'
        },
        buttonInner: {
            justifyContent: 'flex-start',
            color: theme.black
        }
    }
})

interface DataItem extends AutocompleteItem {
    label: string;
}

interface IProps<T> {
    uri: string;
    parser: (item: T, index: number) => DataItem;
    value?: string | DataItem;
    placeholder?: string;
    onChange: (value: string) => void;
    label?: string;
    withAsterisk?: boolean;
}

function SearchableSelect<T>({
    uri,
    parser,
    value,
    placeholder,
    onChange,
    label,
    withAsterisk
}: IProps<T>){

    const { ref, width } = useElementSize();
    const { classes } = useStyles(width);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const [ searchValue, setSearchValue ] = React.useState('');
    const [ debounced ] = useDebouncedValue(searchValue, 500);
    const [ data, setData ] = React.useState<DataItem[]>([]);
    const [ loading, setLoading ] = React.useState(false);
    const [ selectedValue, setSelectedValue ] = React.useState<DataItem | null>(null);

    const controllerRef = React.useRef(new AbortController());

    React.useEffect(() => {
        if (!value) {
            setSelectedValue(null);
        } else if (!selectedValue) {

        }
    }, [value])

    React.useEffect(() => {
        try {
            if (debounced) {
                const fetchData = async () => {
                    setLoading(true);
                    const params = {
                        query: debounced,
                    }
                    const queryParams = qs.stringify(params);
                    const { data: responseData } = await axios.get<IResponse<T[]>>(
                        `${uri}?${queryParams}`,
                        {
                            silent: true,
                            signal: controllerRef.current.signal,
                        }
                    );
                    const q = (responseData.result ?? []).map(parser);
                    setData(q);
                    setLoading(false)
                }
                void fetchData();
            }
        } catch (e) {
            setLoading(false)
        }

    }, [debounced]);

    const handleSelect = (item: DataItem) => {
        setSelectedValue(item);
        setSearchValue('');
        setData([]);
        onChange(item.value);
    }

    const renderValue = () => {
        if (!selectedValue && value && typeof value === 'string') {
            return (
                <Chip radius='md' checked={false}>
                    {value}
                </Chip>
            )
        }
        if (value && selectedValue && !searchValue) {
            return (
                <Chip radius='md' checked={false}>
                    {selectedValue.label}
                </Chip>
            )
        }
        return null;
    }

    return (
        <Popover opened={!!data.length} onClose={() => setData([])} width={width}>
            <Popover.Target>
                <div>
                    <div className={classes.label}>
                        {
                            label && (
                                <Text fz='sm' fw={500}>
                                    {label}
                                </Text>
                            )
                        }
                        {
                            withAsterisk && (
                                <Text fz='sm' fw={500} c='red'>
                                    &nbsp;*
                                </Text>
                            )
                        }
                    </div>
                    <div className={classes.inputWrapper} onClick={() => inputRef?.current?.focus?.()} ref={ref}>
                        <div className={classes.icon}>
                            {
                                loading ? <Loader size='xs' variant='oval'/> : <IconSearch size={16}/>
                            }
                        </div>
                        {
                            renderValue()
                        }
                        <input
                            className={classes.input}
                            ref={inputRef}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.currentTarget.value)}
                            placeholder={placeholder}
                        />
                    </div>
                </div>
            </Popover.Target>

            <Popover.Dropdown className={classes.dropdown}>
                {
                    data.map((item) => {
                        return (
                            <Button
                                key={item.value}
                                variant="subtle"
                                color="gray"
                                onClick={() => handleSelect(item)}
                                fullWidth
                                classNames={{
                                    inner: classes.buttonInner
                                }}
                            >
                                {item.label}
                            </Button>
                        )
                    })
                }
            </Popover.Dropdown>
        </Popover>

    )
}


export default SearchableSelect;